/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  ParticipantMetadataRecord,
  ParticipantMetadataProps,
} from 'src/store/generated/participant-metadata-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ConversationParticipantProps {
  id: string;
  name: string;
  conversationId: string;
  messagingAddress: string;
  vendorParticipantId: string;
  metadata: ParticipantMetadataProps | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ConversationParticipantRecord extends Record<ConversationParticipantProps>({
  id: null as any,
  name: null as any,
  conversationId: null as any,
  messagingAddress: null as any,
  vendorParticipantId: null as any,
  metadata: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    conversationId: string;
    messagingAddress: string;
    vendorParticipantId: string;
    metadata: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ConversationParticipantRecord {
    if (json == null) {
      throw new Error('ConversationParticipantRecord.fromJS: parameter cannot be null');
    }
    return new ConversationParticipantRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ConversationParticipantJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'ConversationParticipantJson.name'),
      conversationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'conversationId', '00000000-0000-0000-0000-000000000000'),
        'ConversationParticipantJson.conversationId',
      ),
      messagingAddress: getFromOrThrow(
        json,
        (j) => j.messagingAddress,
        'ConversationParticipantJson.messagingAddress',
      ),
      vendorParticipantId: getFromOrThrow(
        json,
        (j) => j.vendorParticipantId,
        'ConversationParticipantJson.vendorParticipantId',
      ),
      metadata: json.metadata != null ? new ParticipantMetadataRecord(json.metadata) : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    conversationId: string;
    messagingAddress: string;
    vendorParticipantId: string;
    metadata: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ConversationParticipantRecord, Error[]] {
    if (json == null) {
      throw new Error('ConversationParticipantRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ConversationParticipantRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ConversationParticipantJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'ConversationParticipantJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      conversationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'conversationId', '00000000-0000-0000-0000-000000000000'),
            'ConversationParticipantJson.conversationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      messagingAddress: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.messagingAddress,
            'ConversationParticipantJson.messagingAddress',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorParticipantId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.vendorParticipantId,
            'ConversationParticipantJson.vendorParticipantId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      metadata: tryOrDefault(
        () => (json.metadata != null ? new ParticipantMetadataRecord(json.metadata) : null),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ConversationParticipantProps) {
    super(props);
    if (props == null) {
      throw new Error('ConversationParticipantRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ConversationParticipantViewModelBase implements ConversationParticipantProps {
  protected readonly _props: ConversationParticipantProps;
  private _json?: ConversationParticipantProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get conversationId(): string {
    return this._props.conversationId;
  }
  get messagingAddress(): string {
    return this._props.messagingAddress;
  }
  get vendorParticipantId(): string {
    return this._props.vendorParticipantId;
  }
  get metadata(): ParticipantMetadataProps | null {
    return this._props.metadata;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ConversationParticipantProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        conversationId: this._props.conversationId,
        messagingAddress: this._props.messagingAddress,
        vendorParticipantId: this._props.vendorParticipantId,
        metadata: this._props.metadata,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllConversationParticipantEntities = (state: AppState) =>
  state.entityStores.conversationParticipant.entities;

export const selectConversationParticipantById = (id: string) => (state: AppState) =>
  state.entityStores.conversationParticipant.entities.get(id);

export const selectConversationParticipantMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.conversationParticipant.meta.get(id);

export const selectConversationParticipantAndMetadataById = (id: string) =>
  createSelector(
    selectConversationParticipantById(id),
    selectConversationParticipantMetadataById(id),
    (conversationParticipant, meta) => ({ conversationParticipant, meta }),
  );

export function selectConversationParticipantEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllConversationParticipantEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ConversationParticipantRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectConversationParticipantEntitiesByName(name: string) {
  return createSelector(selectAllConversationParticipantEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectConversationParticipantEntitiesByConversationId(conversationId: string) {
  return createSelector(selectAllConversationParticipantEntities, (entities) =>
    entities.filter((e) => e.conversationId === conversationId).valueSeq(),
  );
}

export function selectConversationParticipantEntitiesByMessagingAddress(messagingAddress: string) {
  return createSelector(selectAllConversationParticipantEntities, (entities) =>
    entities.filter((e) => e.messagingAddress === messagingAddress).valueSeq(),
  );
}

export function selectConversationParticipantEntitiesByVendorParticipantId(
  vendorParticipantId: string,
) {
  return createSelector(selectAllConversationParticipantEntities, (entities) =>
    entities.filter((e) => e.vendorParticipantId === vendorParticipantId).valueSeq(),
  );
}

//#endregion Selectors
